/**
 * The fields a gRPC or WebSocket request keeps beyond the HTTP ones (card
 * 5258c71d). They used to live only in the open tab: Save sent the HTTP fields,
 * so closing the tab, another device and git never saw them.
 *
 * Local workspaces store a request as one JSON record and both git layouts
 * pass unknown fields through, so these travel as plain request fields. The
 * cloud API casts an allowlist and drops them (and `type`), so on the way to
 * the server they ride in `settings.request_protocol`, and
 * hydrateRequestFromApi puts them back.
 */
export const PROTOCOL_FIELDS = Object.freeze({
  grpc: ['grpcAddress', 'grpcUseTLS', 'grpcRequestJson', 'grpcMetadata', 'protoPath', 'grpcService', 'grpcMethod'],
  websocket: ['wsHeaders', 'wsSettings'],
});

const SETTINGS_KEY = 'request_protocol';

/** `type` + the protocol fields of a gRPC / WebSocket request; {} for any other kind. */
export function protocolFieldsOf(source) {
  const type = source?.type;
  const keys = PROTOCOL_FIELDS[type];
  if (!keys) return {};
  const out = { type };
  for (const key of keys) if (source[key] !== undefined) out[key] = source[key];
  return out;
}

/** The request's settings with its protocol fields packed in (for the cloud API). */
export function settingsWithProtocol(settings, source) {
  const next = { ...(settings || {}) };
  const fields = protocolFieldsOf(source);
  if (Object.keys(fields).length) next[SETTINGS_KEY] = fields;
  else delete next[SETTINGS_KEY];
  return next;
}

/** Settings without the packed block (it is unpacked into fields on read). */
export function settingsWithoutProtocol(settings) {
  if (!settings || typeof settings !== 'object' || !(SETTINGS_KEY in settings)) return settings || {};
  const { [SETTINGS_KEY]: _packed, ...rest } = settings;
  return rest;
}

/** Fields unpacked from a request that came from the cloud API; a field already on the request wins. */
export function protocolFieldsFromSettings(apiRequest) {
  const packed = apiRequest?.settings?.[SETTINGS_KEY];
  if (!packed || typeof packed !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(packed)) {
    const present = apiRequest[key];
    // The server answers `type` as 'http' (or not at all) until it stores the column.
    if (present === undefined || present === null || (key === 'type' && present === 'http')) out[key] = value;
  }
  return out;
}
